import classNames from 'classnames';
import React, {useEffect, useRef} from 'react';
import Button from '../../../../incase-domain/components/button/Button';
import {CharacterCounterProps} from './InputWithCharacterCounter';
import {useCharacterCounter} from '../../../../hooks/useCharacterCounter';

interface TextAreaCharacterCounterProps extends CharacterCounterProps {
    maxLines?: number;
}

function TextAreaWithCharacterCounter(props: TextAreaCharacterCounterProps): JSX.Element {
    const {
        value,
        onChange,
        onBlur,
        validator,
        onKeyUp,
        placeholder,
        className,
        maxLength,
        onLimitChange,
        disabled,
        comparisonType,
        dataCy,
        leading,
        trailing,
        enableSubmitByEnter,
        onEnterPress,
        maxLines = 5,
        error,
    } = props;

    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    }, [value]);

    const {isOverLimit, hasError, setHasError} = useCharacterCounter({
        value,
        maxLength,
        comparisonType,
        onLimitChange,
        validator,
    });

    function handleKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>): void {
        if (!enableSubmitByEnter) return;
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            onEnterPress?.(event);
        }
    }

    return (
        <>
            <div className={classNames(className, {'is-error': hasError})}>
                {leading && (
                    <Button
                        className="c-sidepanel__input__leading"
                        leading={leading}
                        size="xs"
                        variant="ghost"
                        square
                        disablePointerEvents
                    />
                )}
                <textarea
                    ref={textareaRef}
                    rows={1}
                    style={maxLines ? {maxHeight: `${1.75 * maxLines}em`, overflowY: 'auto'} : undefined}
                    placeholder={placeholder}
                    value={value}
                    onChange={(e) => {
                        onChange(e.target.value);
                        if (hasError && validator) setHasError(!validator(e.target.value));
                    }}
                    onBlur={(e) => {
                        onBlur?.(e.target.value);
                        if (validator) setHasError(!validator(e.target.value));
                    }}
                    onKeyDown={handleKeyDown}
                    onKeyUp={onKeyUp}
                    disabled={disabled}
                    data-cy={dataCy}
                    className="c-sidepanel__input_field"
                />
                <div className="c-sidepanel__input__trailing">{trailing}</div>
            </div>
            <div className="u-flex u-gap-1 u-v-center u-h-between">
                <div className="c-characterCounter c-characterCounter--alert">{error || (hasError ? 'Invalid input' : '')}</div>
                {maxLength ? (
                    <div
                        className={classNames('c-characterCounter', {
                            'c-characterCounter--alert': isOverLimit || hasError,
                            'u-hide': disabled,
                        })}
                    >
                        {value.length}/{maxLength}
                    </div>
                ) : null}
            </div>
        </>
    );
}

export default TextAreaWithCharacterCounter;
